"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { LogOut, Menu, X } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

import { accountNav, primaryNav } from "@/components/shell/nav";
import { countForHref, NO_COUNTS } from "@/components/shell/nav-counts";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { logout } from "@/features/auth/api/auth-client";
import { useCurrentUser } from "@/features/auth/hooks/use-current-user";
import { useUnreadCount } from "@/features/chat/hooks/use-chat";
import { CountBadge } from "@/features/notifications/components/count-badge";
import { useNotificationCounts } from "@/features/notifications/hooks/use-notification-counts";

/**
 * Row styling shared by every link in the drawer. Full-width, 44px tall — the
 * touch-target floor — rather than the desktop nav's 32px pills, which are
 * tuned for a mouse.
 */
const row =
  "flex h-11 items-center justify-between gap-3 rounded-lg border px-3 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";

/**
 * Active/idle treatment lifted from `AccountTabsNav` so the drawer marks the
 * current section exactly the way the secondary tab row does — solid
 * `--nav-active` border carrying the state, /10 wash as decoration.
 */
const rowState = (active: boolean) =>
  active
    ? "border-nav-active bg-nav-active/10 font-semibold text-foreground"
    : "border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground";

const sectionLabel =
  "px-1 pb-1.5 pt-4 text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground";

/**
 * The mobile primary navigation (below `md`).
 *
 * The header's inline nav track is `hidden md:flex`, and the "My account"
 * dropdown is nested inside it — so on a phone the app had NO way to move
 * between sections at all (HighBid: "I don't even see the main menu on
 * mobile"). This is that missing surface: a hamburger in the header that opens
 * a slide-out drawer carrying BOTH link sets — the four primary sections and
 * the full account menu, with the same count badges as the desktop dropdown.
 *
 * Built on the shared Radix dialog primitives rather than a hand-rolled overlay
 * (the pattern `AdminShell` uses) so focus trapping, Escape-to-close, scroll
 * lock, and focus restoration come for free — and because it renders in a
 * portal, no ancestor's `overflow` or stacking context can clip it.
 */
export function MobileNav() {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const { user, isAuthenticated } = useCurrentUser();
  const { data: counts } = useNotificationCounts();
  const { data: chatUnread } = useUnreadCount({ enabled: isAuthenticated });

  // Close on navigation. Radix keeps the dialog mounted across a client-side
  // route change, so without this the drawer stays open over the new page.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const isActive = (href: string) =>
    pathname === href || pathname.startsWith(`${href}/`);

  const linkRow = (
    item: { label: string; href: string },
    { badge = false }: { badge?: boolean } = {},
  ) => {
    const count = badge
      ? countForHref(item.href, counts ?? NO_COUNTS, chatUnread ?? 0)
      : 0;
    const active = isActive(item.href);
    return (
      <li key={item.href}>
        <Link
          href={item.href}
          aria-current={active ? "page" : undefined}
          className={cn(row, rowState(active))}
        >
          <span className="truncate">{item.label}</span>
          <CountBadge count={count} label={`${count} for ${item.label}`} />
        </Link>
      </li>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Open menu"
          // `md:hidden` is the exact inverse of the nav track's `md:flex`, so
          // one and only one of the two is ever reachable.
          className="-mr-1 size-9 shrink-0 md:hidden"
        >
          <Menu aria-hidden="true" className="size-5" />
        </Button>
      </DialogTrigger>

      <DialogPortal>
        <DialogOverlay className="md:hidden" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            "fixed inset-y-0 right-0 z-50 flex w-[min(20rem,85vw)] flex-col border-l border-hairline bg-background shadow-lg md:hidden",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
          )}
        >
          <div className="flex h-16 shrink-0 items-center justify-between border-b border-hairline px-4">
            <DialogTitle className="text-sm font-bold uppercase tracking-[0.14em] text-muted-foreground">
              Menu
            </DialogTitle>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Close menu">
                <X aria-hidden="true" className="size-5" />
              </Button>
            </DialogPrimitive.Close>
          </div>

          {/* The account section can run long, so the body scrolls on its own
           * and the header/footer stay pinned. */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-4">
            <nav aria-label="Primary mobile">
              <p className={sectionLabel}>Browse</p>
              <ul className="flex flex-col gap-1.5">
                {primaryNav.map((item) => linkRow(item))}
              </ul>
            </nav>

            {/* Signed out: the header's Sign in / Sign up buttons are still
             * visible, so the drawer carries the browse links only. */}
            {isAuthenticated && user ? (
              <nav aria-label="Account mobile">
                {accountNav.map((group) => (
                  <div key={group.label ?? "general"}>
                    <p className={sectionLabel}>{group.label ?? "Account"}</p>
                    <ul className="flex flex-col gap-1.5">
                      {group.items.map((item) => linkRow(item, { badge: true }))}
                    </ul>
                  </div>
                ))}
              </nav>
            ) : null}
          </div>

          {isAuthenticated && user ? (
            <div className="shrink-0 border-t border-hairline p-3">
              <Button
                variant="ghost"
                onClick={() => void logout()}
                className="h-11 w-full justify-start gap-2 text-muted-foreground"
              >
                <LogOut aria-hidden="true" className="size-4" />
                Logout
              </Button>
            </div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
